import { createApiRef, DiscoveryApi, IdentityApi } from '@backstage/frontend-plugin-api';

export type HealthStatus = {
    status: 'Healthy' | 'Unhealthy';
    message: string;
    component: string
    timestamp: Date
    name: string
    healthCheckName: string
  }

export interface ClientOptions {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }

export interface MonitorApi {
    getHealthStatus: () => Promise<Array<HealthStatus>>
    getHealthStatusForComponent: (component: string) => Promise<Array<HealthStatus>>
}

export const monitorApiRef = createApiRef<MonitorApi>({
  id: 'plugin.ewii-azure.monitor',
});

let healthStatusCache = new Map<string, HealthStatus[]>();

export class DefaultMonitorApi implements MonitorApi {

    private readonly discoveryApi: DiscoveryApi;
    private readonly identityApi: IdentityApi;

    constructor(options: ClientOptions) {
        this.discoveryApi = options.discoveryApi;
        this.identityApi = options.identityApi;
      }

  async getHealthStatus(): Promise<Array<HealthStatus>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();

    const response = await fetch(`${baseUrl}/healthstatus`, {
        method: 'GET',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
    if (!response.ok) {
      throw new Error(`Failed to fetch health status: ${response.statusText}`);
    }
    const data = await response.json();

    const healthStatus = data.map((item: any) => ({
        ...item,
        timestamp: new Date(item.timestamp),
      }));


    healthStatusCache.clear(); // Clear the cache before populating it

    // Cache the health status data
    healthStatus.forEach((item: HealthStatus) => {
        healthStatusCache.set(item.component, [
            ...(healthStatusCache.get(item.component) || []),
            item,
        ]);
    });

    return healthStatus 
  }

  async getHealthStatusForComponent(component: string): Promise<Array<HealthStatus>> {

    // Check if the health status for the component is already cached
    if (healthStatusCache.has(component)) {
        return healthStatusCache.get(component) || [];
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();

    const response = await fetch(`${baseUrl}/healthstatus/${component}`, {
        method: 'GET',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
    if (!response.ok) {
      throw new Error(`Failed to fetch health status for component ${component}: ${response.statusText}`);
    }
    const data = await response.json();

    return data.map((item: any) => ({
        ...item,
        timestamp: new Date(item.timestamp),
      }));;
  }
}

export interface ReleaseHistory {
  releaseNumber: string;
  releaseUrl: string;
  releaseDate: Date;
  toBranch: string;
  fromBranch: string;
  toCommit: string;
  fromCommit: string;
  changeLogUrl: string;
  changeLogFile: string;
  status: 'deployed' | 'staged' | 'cancelled';
}

export interface CombinedReleaseHistory extends ReleaseHistory {
  component: string;
  lifeCycle: string;
}

export interface DevOpsApi {
  getReleaseHistory: (component: string) => Promise<Array<ReleaseHistory>>;
  getCombinedReleaseHistory: (lifeCycle: string, fromDate?: Date) => Promise<Array<CombinedReleaseHistory>>;
  getChangeLogMarkdown: (component: string, fileName: string) => Promise<string>;
}

export const devOpsApiRef = createApiRef<DevOpsApi>({
  id: 'plugin.ewii-azure.devops',
});

export class DefaultDevOpsApi implements DevOpsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: ClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async getReleaseHistory(component: string): Promise<Array<ReleaseHistory>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();
    const url = `${baseUrl}/releasehistory/${component}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch release history from ${component}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.map((item: any) => ({
      ...item,
      releaseDate: item.releaseDate ? new Date(item.releaseDate) : undefined,
    }));
  }

  async getCombinedReleaseHistory(lifeCycle: string, fromDate?: Date): Promise<Array<CombinedReleaseHistory>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();
    const url = `${baseUrl}/releasehistory/combined/${lifeCycle}?fromDate=${fromDate ? fromDate.toISOString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch combined release history from ${lifeCycle}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.map((item: any) => ({
      ...item,
      releaseDate: item.releaseDate ? new Date(item.releaseDate) : undefined,
    }));
  }

  async getChangeLogMarkdown(component: string, fileName: string): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();
    const url = `${baseUrl}/change-log/${component}/${fileName}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch change log markdown: ${response.statusText}`);
    }
    return await response.text();
  }
}

export interface FeatureToggle {
  key: string;
  value: string | boolean | number;
  enabled: boolean;
  label?: string;
  contentType?: string;
  lastModified?: Date;
  environment: string;
  etag?: string;
}

export interface AppConfigApi {
  getFeatureToggles: () => Promise<Array<FeatureToggle>>;
  getFeatureTogglesForEnvironment: (environment: string) => Promise<Array<FeatureToggle>>;
  getFeatureToggleByKey: (key: string) => Promise<FeatureToggle | undefined>;
}

export const appConfigApiRef = createApiRef<AppConfigApi>({
  id: 'plugin.ewii-azure.appconfig',
});

export class DefaultAppConfigApi implements AppConfigApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: ClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async getFeatureToggles(): Promise<Array<FeatureToggle>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();

    const response = await fetch(`${baseUrl}/featuretoggles`, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch feature toggles: ${response.statusText}`);
    }
    const data = await response.json();
    return data.map((item: any) => ({
      ...item,
      lastModified: item.lastModified ? new Date(item.lastModified) : undefined,
    }));
  }

  async getFeatureTogglesForEnvironment(environment: string): Promise<Array<FeatureToggle>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();

    const response = await fetch(`${baseUrl}/featuretoggles/${environment}`, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch feature toggles for environment ${environment}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.map((item: any) => ({
      ...item,
      lastModified: item.lastModified ? new Date(item.lastModified) : undefined,
    }));
  }

  async getFeatureToggleByKey(key: string): Promise<FeatureToggle | undefined> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ewii-azure');
    const { token } = await this.identityApi.getCredentials();

    const response = await fetch(`${baseUrl}/featuretoggle/${key}`, {
      method: 'GET',
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    });
    if (!response.ok) {
      if (response.status === 404) {
        return undefined;
      }
      throw new Error(`Failed to fetch feature toggle ${key}: ${response.statusText}`);
    }
    const data = await response.json();
    return {
      ...data,
      lastModified: data.lastModified ? new Date(data.lastModified) : undefined,
    };
  }
}

